import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, Loader2, Heart, ShoppingCart, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { ProductImageGallery } from '../components/ProductImageGallery';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priceRanges = [
  { label: 'Under ₹5,000', min: 0, max: 5000 },
  { label: '₹5,000 - ₹8,000', min: 5000, max: 8000 },
  { label: '₹8,000 - ₹12,000', min: 8000, max: 12000 },
  { label: 'Above ₹12,000', min: 12000, max: null }
];

const sortOptions = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Top Rated', value: 'rating' }
];

export const SearchResults = ({ onAddToCart, onAddToWishlist }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  // Get filter values from URL
  const searchQuery = searchParams.get('q') || '';
  const selectedCategory = searchParams.get('category') || '';
  const selectedPriceRange = searchParams.get('price') || '';
  const selectedSort = searchParams.get('sort') || 'newest';

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch products with filters
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedCategory) params.append('category', selectedCategory);
        if (selectedSort) params.append('sort', selectedSort);
        
        // Handle price range
        if (selectedPriceRange) {
          const range = priceRanges.find(r => r.label === selectedPriceRange);
          if (range) {
            params.append('min_price', range.min);
            if (range.max) params.append('max_price', range.max);
          }
        }

        const response = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [searchQuery, selectedCategory, selectedPriceRange, selectedSort]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleAddToCart = (product) => {
    onAddToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleAddToWishlist = (product) => {
    onAddToWishlist(product);
    toast.success(`${product.name} added to wishlist!`);
  };

  const hasActiveFilters = searchQuery || selectedCategory || selectedPriceRange;

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-seasons text-[#2C2C2C]">
              {searchQuery ? `Results for "${searchQuery}"` : 'All Products'}
            </h1>
            <p className="text-[#8B7D6B] font-montserrat mt-1">
              {products.length} product{products.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={selectedSort}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="appearance-none bg-white border border-[#E8E5E0] rounded-lg px-4 py-2 pr-10 font-montserrat text-sm text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#8B7D6B] pointer-events-none" />
            </div>

            {/* Mobile Filter Button */}
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden border-[#E8E5E0]">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle className="font-seasons">Filters</SheetTitle>
                </SheetHeader>
                <FilterSidebar
                  categories={categories}
                  selectedCategory={selectedCategory}
                  selectedPriceRange={selectedPriceRange}
                  onCategoryChange={(cat) => updateFilter('category', cat)}
                  onPriceChange={(price) => updateFilter('price', price)}
                  onClear={clearAllFilters}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-sm text-[#8B7D6B] font-montserrat">Active filters:</span>
            {searchQuery && (
              <Badge variant="secondary" className="bg-[#F5F3F0] text-[#6B4E37]">
                Search: {searchQuery}
                <button onClick={() => updateFilter('q', '')} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {selectedCategory && (
              <Badge variant="secondary" className="bg-[#F5F3F0] text-[#6B4E37]">
                {selectedCategory}
                <button onClick={() => updateFilter('category', '')} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {selectedPriceRange && (
              <Badge variant="secondary" className="bg-[#F5F3F0] text-[#6B4E37]">
                {selectedPriceRange}
                <button onClick={() => updateFilter('price', '')} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            <button
              onClick={clearAllFilters}
              className="text-sm text-[#C4B5A0] hover:text-[#B0A090] font-montserrat underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Filter Sidebar */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <FilterSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              selectedPriceRange={selectedPriceRange}
              onCategoryChange={(cat) => updateFilter('category', cat)}
              onPriceChange={(price) => updateFilter('price', price)}
              onClear={clearAllFilters}
            />
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-[#C4B5A0]" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <Search className="h-12 w-12 text-[#C4B5A0] mx-auto mb-4" />
                <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-2">No products found</h2>
                <p className="text-[#8B7D6B] font-montserrat mb-6">Try adjusting your filters or search terms</p>
                <Button onClick={clearAllFilters} className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <Card
                    key={product.id}
                    className="group relative bg-white border-none shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
                    onMouseEnter={() => setHoveredId(product.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div 
                      className="relative overflow-hidden aspect-[3/4] bg-[#F5F3F0] cursor-pointer"
                      onClick={() => handleProductClick(product)}
                    >
                      <ProductImageGallery images={product.images} fallback={product.image} alt={product.name} compact className="w-full h-full" />
                      
                      {product.discount && (
                        <Badge className="absolute top-4 left-4 bg-[#6B4E37] text-white font-montserrat text-xs">
                          {product.discount}
                        </Badge>
                      )}

                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handleAddToWishlist(product); }}
                      >
                        <Heart className="h-4 w-4" />
                      </Button>

                      {/* Share on WhatsApp */}
                      <div className="absolute top-4 right-14 opacity-0 group-hover:opacity-100 transition-opacity">
                        <WhatsAppButton 
                          variant="share-icon" 
                          productName={product.name}
                          productPrice={product.price}
                        />
                      </div>

                      {/* Out of Stock Badge */}
                      {product.stock <= 0 && (
                        <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-montserrat tracking-wider uppercase px-3 py-1.5 rounded shadow-lg" data-testid={`oos-badge-${product.id}`}>
                          Out of Stock
                        </div>
                      )}

                      <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-300 ${hoveredId === product.id ? 'opacity-100' : 'opacity-0'}`}>
                        <Button
                          data-testid={`add-to-cart-btn-${product.id}`}
                          disabled={product.stock <= 0}
                          className={`w-full font-montserrat text-xs tracking-wider uppercase ${
                            product.stock <= 0
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-white text-[#2C2C2C] hover:bg-[#C4B5A0] hover:text-white'
                          }`}
                          onClick={(e) => { e.stopPropagation(); if (product.stock <= 0) return; handleAddToCart(product); }}
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="text-[#8B7D6B] font-montserrat text-xs tracking-wider uppercase mb-2">
                        {product.category}
                      </p>
                      <h3 className="font-seasons text-lg text-[#2C2C2C] mb-3 line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < Math.floor(product.rating) ? 'fill-[#C4B5A0] text-[#C4B5A0]' : 'text-[#D4C4B0]'}`} />
                          ))}
                        </div>
                        <span className="text-xs text-[#8B7D6B] font-montserrat">({product.reviews})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-seasons text-[#2C2C2C]">₹{product.price}</span>
                        <span className="text-sm font-montserrat text-[#8B7D6B] line-through">₹{product.originalPrice}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ProductDetailModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedProduct(null); }}
        onAddToCart={onAddToCart}
        onAddToWishlist={onAddToWishlist}
      />
    </div>
  );
};

// Filter Sidebar Component
const FilterSidebar = ({ categories, selectedCategory, selectedPriceRange, onCategoryChange, onPriceChange, onClear }) => {
  return (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <h3 className="font-seasons text-lg text-[#2C2C2C] mb-4">Categories</h3>
        <div className="space-y-2">
          <button
            onClick={() => onCategoryChange('')}
            className={`block w-full text-left px-3 py-2 rounded-lg font-montserrat text-sm transition-colors ${
              !selectedCategory ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
            }`}
          >
            All Categories
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => onCategoryChange(cat.name)}
              className={`block w-full text-left px-3 py-2 rounded-lg font-montserrat text-sm transition-colors ${
                selectedCategory === cat.name ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="font-seasons text-lg text-[#2C2C2C] mb-4">Price Range</h3>
        <div className="space-y-2">
          <button
            onClick={() => onPriceChange('')}
            className={`block w-full text-left px-3 py-2 rounded-lg font-montserrat text-sm transition-colors ${
              !selectedPriceRange ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
            }`}
          >
            All Prices
          </button>
          {priceRanges.map((range) => (
            <button
              key={range.label}
              onClick={() => onPriceChange(range.label)}
              className={`block w-full text-left px-3 py-2 rounded-lg font-montserrat text-sm transition-colors ${
                selectedPriceRange === range.label ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={onClear}
        variant="outline"
        className="w-full border-[#E8E5E0] text-[#8B7D6B]"
      >
        Clear All Filters
      </Button>
    </div>
  );
};
